import React, { useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { Box, HStack } from "@chakra-ui/layout";

import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { Checkbox } from "@chakra-ui/checkbox";
import { useCheckboxState } from "../../hooks/useCheckboxState";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { Button, SkeletonText, VStack, Text } from "@chakra-ui/react";
import { PlusIcon } from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { useAdminApiUpdateRequest } from "../../hooks/useApiUpdatedRequest";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { RegistrationFormConfigurationsTable } from "../../components/RegistrationFormConfiguration/RegistrationFormConfigurationTable";
import { useSearchInput } from "../../hooks/useSearchInput";
import { BusinessMenu } from "../../components/Business/BusinessMenu";

export const RegistrationFormConfigurations = () => {
  const business = useBusinessContext();
  const apiClient = useAdminApiClient();
  const navigate = useNavigate();
  const {
    checked: showDeletedRegistrationFormConfigurations,
    onChange: onChangeDeletedRegisrationCheck,
  } = useCheckboxState(false);

  const { showErrorToast, showSuccessToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();

  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search digital membership registration forms...",
  });

  const {
    items: registrations,
    refresh,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) =>
      apiClient.getBusinessRegistrationFormConfigurations(
        business.BusinessId!,
        {
          q: debouncedQuery,
          includeDeleted: showDeletedRegistrationFormConfigurations
            ? "true"
            : "false",
        }
      ),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getBusinessRegistrationFormConfigurations(
        business.BusinessId!,
        {
          q: debouncedQuery,
          includeDeleted: showDeletedRegistrationFormConfigurations
            ? "true"
            : "false",
          continuationToken,
        }
      ),
    [
      business.BusinessId,
      debouncedQuery,
      showDeletedRegistrationFormConfigurations,
    ]
  );

  const deleteRegistrationRequest = useAdminApiUpdateRequest(
    (apiClient, registrationId: string) =>
      apiClient.deleteBusinessRegistrationFormConfiguration(
        business.BusinessId!,
        registrationId
      ),
    [registrations]
  );

  const restoreRegistrationRequest = useAdminApiUpdateRequest(
    (apiClient, registrationId: string) =>
      apiClient.restoreBusinessRegistrationFormConfiguration(
        business.BusinessId!,
        registrationId
      ),
    [registrations]
  );

  const cloneRegistrationRequest = useAdminApiUpdateRequest(
    (apiClient, registrationId: string) =>
      apiClient.cloneBusinessRegistrationFormConfiguration(
        business.BusinessId!,
        registrationId
      ),
    [registrations]
  );

  const onClickNewRegisration = () =>
    navigate(
      `/admin/businesses/${business.BusinessId}/registration-form-configurations/new`
    );

  const onClickEditRegistration = (
    registration: CherryHubAdminApi.RegistrationFormConfigurationSummaryItem
  ) =>
    navigate(
      `/admin/businesses/${business.BusinessId}/registration-form-configurations/${registration.Meta?.Id}`
    );

  useEffect(() => {
    if (deleteRegistrationRequest.result) {
      if (deleteRegistrationRequest.result.ok) {
        showSuccessToast("Digital membership registration form deleted.");
        refresh();
      } else {
        showErrorToast(
          deleteRegistrationRequest.result.message ??
            "Failed to delete digital membership registration form."
        );
      }
    }
  }, [deleteRegistrationRequest.result, refresh]);

  const onClickDeleteRegistration = useCallback(
    (
      registration: CherryHubAdminApi.RegistrationFormConfigurationSummaryItem
    ) =>
      showConfirmationDialog(
        "Delete Digital Membership Registration Form?",
        `Are you sure you wish to delete this digital membership registration form: ${registration.DisplayName}?`,
        "Delete"
      ).then(({ ok }) => {
        if (ok) {
          deleteRegistrationRequest.request(registration.Meta?.Id!);
        }
      }),
    [
      showSuccessToast,
      showErrorToast,
      showConfirmationDialog,
      apiClient,
      refresh,
    ]
  );

  useEffect(() => {
    if (restoreRegistrationRequest.result) {
      if (restoreRegistrationRequest.result.ok) {
        showSuccessToast(
          "Deleted digital membership registration form restored."
        );
        refresh();
      } else {
        showErrorToast(
          restoreRegistrationRequest.result.message ??
            "Failed to restore deleted digital membership registration form."
        );
      }
    }
  }, [restoreRegistrationRequest.result, refresh]);

  const onClickRestoreRegistrationFormConfiguration = useCallback(
    (
      registration: CherryHubAdminApi.RegistrationFormConfigurationSummaryItem
    ) =>
      showConfirmationDialog(
        "Restore Digital Membership Registration Form?",
        `Are you sure you wish to restore this deleted digital membership registration form: ${registration.DisplayName}?`,
        "Restore"
      ).then(({ ok }) => {
        if (ok) {
          restoreRegistrationRequest.request(registration.Meta?.Id!);
        }
      }),
    [
      showSuccessToast,
      showErrorToast,
      showConfirmationDialog,
      apiClient,
      refresh,
    ]
  );

  useEffect(() => {
    if (cloneRegistrationRequest.result) {
      if (cloneRegistrationRequest.result.ok) {
        showSuccessToast(
          "Digital membership registration form successfully cloned and opened for preview"
        );
        const clonedId = cloneRegistrationRequest.result.data?.Meta?.Id;
        if (clonedId) {
          navigate(
            `/admin/businesses/${business.BusinessId}/registration-form-configurations/${clonedId}`
          );
        }
      } else {
        showErrorToast(
          cloneRegistrationRequest.result.message ??
            "Failed to clone digital membership registration form."
        );
      }
    }
  }, [cloneRegistrationRequest.result, navigate, business.BusinessId]);

  const onClickCloneRegistration = useCallback(
    (
      registration: CherryHubAdminApi.RegistrationFormConfigurationSummaryItem
    ) =>
      showConfirmationDialog(
        "Clone Digital Membership Registration Form?",
        `Are you sure you wish to clone this digital membership registration form: ${registration.DisplayName}?`,
        "Clone"
      ).then(({ ok }) => {
        if (ok) {
          cloneRegistrationRequest.request(registration.Meta?.Id!);
        }
      }),
    [showConfirmationDialog, cloneRegistrationRequest]
  );

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          Digital Membership Registration Forms
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack w="100%" alignItems="flex-start">
          <BusinessBreadcrumbs variant="pageHeading" mb={4}>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${business.BusinessId}/registration-form-configurations`}
              >
                Digital Membership Registration Forms
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>
          <HStack w="100%">
            <Box flex="4" maxWidth="400px">
              {searchInputElement}
            </Box>
            <Box flex="1">
              <Checkbox
                checked={showDeletedRegistrationFormConfigurations}
                onChange={onChangeDeletedRegisrationCheck}
                title="Include deleted digital membership registration form in the list."
              >
                <Text fontSize="sm">Include Deleted</Text>
              </Checkbox>
            </Box>
            <Box marginLeft="auto">
              <BusinessMenu
                businessId={business.Meta?.Id!}
                leftIcon={<PlusIcon />}
                onClick={onClickNewRegisration}
              >
                New Digital Membership Registration Form
              </BusinessMenu>
            </Box>
          </HStack>

          <RegistrationFormConfigurationsTable
            registrations={registrations}
            isLoading={isLoading}
            disableActions={
              deleteRegistrationRequest.isLoading ||
              cloneRegistrationRequest.isLoading
            }
            debouncedQuery={debouncedQuery}
            onClickEditRegistration={onClickEditRegistration}
            onClickCloneRegistration={onClickCloneRegistration}
            onClickDeleteRegistration={onClickDeleteRegistration}
            onClickRestoreRegistration={
              onClickRestoreRegistrationFormConfiguration
            }
          />
          {fetchNextPage && !isLoading && (
            <Box w="100%">
              {!isLoadingPage && (
                <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                  Load more...
                </Button>
              )}
              {isLoadingPage && (
                <HStack width="100%" boxShadow="lg">
                  <Box width="90%" padding="4" pr="0">
                    <SkeletonText mt="4" noOfLines={4} spacing="4" />
                  </Box>
                  <Box width="5%" pt="4" pb="4">
                    <SkeletonText mt="4" noOfLines={4} spacing="4" />
                  </Box>
                  <Box width="5%" pt="4" pb="4" pr="4">
                    <SkeletonText mt="4" noOfLines={4} spacing="4" />
                  </Box>
                </HStack>
              )}
            </Box>
          )}
          {error != null && (
            <ErrorMessage>
              {error ??
                "An error was encountered while searching digital membership registration form."}
            </ErrorMessage>
          )}
          {(!registrations || isLoading) && <LoadingIndicator />}
          {confirmationElement}
        </VStack>
      </PageContent>
    </>
  );
};
